import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Alert } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useSecurityModals } from "../../context/SecurityModalsContext";
import { userNeedsTransactionPin } from "../../utils/auth/permissionConstants";

const BANNER_STATE_NEEDS_SETUP = "needs-setup";
const BANNER_STATE_EXPIRING_SOON = "expiring-soon";
const BANNER_STATE_EXPIRED = "expired";

const HOURS_UNTIL_WARNING = 48;

const dismissKeyFor = (state, expiryDateIso) => {
  // The expiry date is part of the key so dismissing the 48-hour warning does
  // not carry over into the "already expired" banner on the following day, or
  // silence a fresh 48-hour warning after the next rotation.
  const suffix = expiryDateIso ?? "no-date";
  return `transactionPinBanner:${state}:${suffix}`;
};

const readDismissed = (key) => {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const writeDismissed = (key) => {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage may be blocked (private mode, storage disabled); a
    // banner that reappears is a better failure mode than a crash.
  }
};

const TransactionPinExpiryBanner = () => {
  const authUser = useSelector((state) => state.auth.authUser);
  const location = useLocation();
  const { openChangePin, openForgotPin } = useSecurityModals();

  const { state, hoursRemaining, expiryDateIso } = useMemo(() => {
    if (!userNeedsTransactionPin(authUser)) {
      return { state: null, hoursRemaining: null, expiryDateIso: null };
    }

    if (authUser.has_transaction_pin === false) {
      return { state: BANNER_STATE_NEEDS_SETUP, hoursRemaining: null, expiryDateIso: null };
    }

    const expiryIso = authUser.transaction_pin_expiry_date;
    if (!expiryIso) {
      return { state: null, hoursRemaining: null, expiryDateIso: null };
    }

    const expiryMs = new Date(expiryIso).getTime();
    if (Number.isNaN(expiryMs)) {
      return { state: null, hoursRemaining: null, expiryDateIso: null };
    }

    const diffHours = (expiryMs - Date.now()) / (1000 * 60 * 60);

    if (diffHours <= 0) {
      return { state: BANNER_STATE_EXPIRED, hoursRemaining: diffHours, expiryDateIso: expiryIso };
    }

    if (diffHours <= HOURS_UNTIL_WARNING) {
      return { state: BANNER_STATE_EXPIRING_SOON, hoursRemaining: diffHours, expiryDateIso: expiryIso };
    }

    return { state: null, hoursRemaining: diffHours, expiryDateIso: expiryIso };
  }, [authUser]);

  const dismissKey = state ? dismissKeyFor(state, expiryDateIso) : null;
  const [dismissed, setDismissed] = useState(() => (dismissKey ? readDismissed(dismissKey) : false));

  // The set-PIN screen is where the user is actively doing the thing the
  // needs-setup banner nags about, so suppress the banner there to avoid a
  // duplicate ask above the very form that answers it.
  if (location.pathname === "/set-transaction-pin") return null;

  if (!state || dismissed) return null;

  const handleClose = () => {
    if (dismissKey) writeDismissed(dismissKey);
    setDismissed(true);
  };

  if (state === BANNER_STATE_NEEDS_SETUP) {
    return (
      <Alert variant="info" dismissible onClose={handleClose} className="mb-3">
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
          <div className="flex-grow-1">
            <strong>Set your Transaction PIN.</strong>{" "}
            You need a six-digit PIN before you can send money, pay bills, or buy goods.
          </div>
          <Link to="/set-transaction-pin" className="btn btn-sm btn-info text-white">
            Set PIN now
          </Link>
        </div>
      </Alert>
    );
  }

  if (state === BANNER_STATE_EXPIRING_SOON) {
    const hoursLabel = Math.max(1, Math.ceil(hoursRemaining));
    return (
      <Alert variant="warning" dismissible onClose={handleClose} className="mb-3">
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
          <div className="flex-grow-1">
            <strong>Your Transaction PIN expires in {hoursLabel} hour{hoursLabel === 1 ? "" : "s"}.</strong>{" "}
            Change it now so you can keep transacting without interruption.
          </div>
          <button type="button" onClick={openChangePin} className="btn btn-sm btn-warning">
            Change PIN
          </button>
        </div>
      </Alert>
    );
  }

  return (
    <Alert variant="danger" dismissible onClose={handleClose} className="mb-3">
      <div className="d-flex flex-column flex-md-row align-items-md-center gap-2">
        <div className="flex-grow-1">
          <strong>Your Transaction PIN has expired.</strong>{" "}
          Reset it to unlock Send Money, Paybill, Buy Goods, and Pochi.
        </div>
        <button type="button" onClick={openForgotPin} className="btn btn-sm btn-danger">
          Reset PIN
        </button>
      </div>
    </Alert>
  );
};

export default TransactionPinExpiryBanner;
